import React, {
  ChangeEvent,
  ReactNode,
  useCallback,
  useEffect,
  useState,
} from "react";
import {
  FormControl,
  FormControlLabel,
  TextField,
  Checkbox,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Button,
  ThemeProvider,
  InputAdornment,
  makeStyles,
} from "@material-ui/core";
import { Block, Search, Check } from "@material-ui/icons";
import { Pagination } from "@material-ui/lab";

function setsAreDifferent(setA: Set<string>, setB: Set<string>) {
  return (
    setA.size !== setB.size || Array.from(setA).some((item) => !setB.has(item))
  );
}
const useStyles = makeStyles((theme) => ({
  search: {
    "& input": {
      padding: 3,
    },
    "& > div": {
      padding: 3,
    },
    backgroundColor: "white",
    borderRadius: 5,
    width: "100%",
  },
  searchDark: {
    borderRadius: 5,
    marginBottom: 10,
    width: "100%",
    display: "flex",
    "& input": {
      padding: 3,
      color: "white",
    },
    "& > div": {
      padding: 3,
      color: "white",
    },
    color: "white",
    backgroundColor: "#282828 !important",
  },
}))

export default function CxSidebarView({
  itemList,
  sortFunction,
  searchAttributes,
  blockedItems,
  excludedItems,
  setBlockedItems,
  formatDisplay,
  onItemClick,
  onAll,
  onNone,
  mode,
}: {
  itemList: Array<{ id: string; name?: string }>;
  sortFunction: (
    arg: Array<{ id: string; name?: string }>
  ) => Array<{ id: string; name?: string }>;
  searchAttributes: Array<string>;
  blockedItems: Set<string>;
  excludedItems: Set<string>;
  setBlockedItems: React.Dispatch<React.SetStateAction<Set<string>>>;
  formatDisplay: (arg: any) => ReactNode;
  onItemClick: (arg: any) => void;
  onAll: () => void;
  onNone: () => void;
  mode: string;
}) {
  const classes = useStyles();
  const [currentPage, setCurrentPage] = useState(1)
  const itemsPerPage = 20;
  const totalPages =  Math.ceil(itemList.length / itemsPerPage)

  const [fitSearch, setFitSearch] = useState<Set<string>>( new Set(itemList.map((item) => item.id)));
  const [filteredItems, setFilteredItems] = useState<Array<{id: string;}>>([]);
  const [blockedFitSearch, setBlockedFitSearch] = useState<Array<{ id: string }>>([]);
    
  const handlePageChange = (event: React.ChangeEvent<unknown>, page: number) => setCurrentPage(page)
  

  const handleSearch = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (itemList === undefined) {
        return;
      }

      const searchText = e.target.value.trim().toLowerCase();

      let newFit;
      if (searchText === "") {
        newFit = itemList.map((item) => item.id);
      } else {
        newFit = itemList
          .filter((item: any) =>
            searchAttributes.some((attribute) =>
              item[attribute]?.toString().toLowerCase().includes(searchText)
            )
          )
          .map((item) => item.id);
      }

      if (setsAreDifferent(new Set(newFit), fitSearch)) {
        setFitSearch(new Set(newFit));
      }
    },
    [itemList, searchAttributes, fitSearch]
  );

  useEffect(() => {
    if (fitSearch === undefined || blockedItems === undefined) {
      return;
    }
    const searchedItems = itemList.filter((item) => fitSearch.has(item.id));
    setFilteredItems(
      sortFunction(searchedItems.filter((item) => !blockedItems.has(item.id)))
    );
    setBlockedFitSearch(
      searchedItems.filter((item) => blockedItems.has(item.id))
    );
  }, [itemList, fitSearch, blockedItems, sortFunction]);

  const clickOnBlock = useCallback(
    (event: React.MouseEvent<HTMLButtonElement, MouseEvent>, item: any) => {
      event.stopPropagation();

      setBlockedItems((oldBlocked) => {
        const newBlocked = new Set(oldBlocked);
        newBlocked.add(item.id);

        return newBlocked;
      });

      if (!excludedItems.has(item.id)) {
        onItemClick(item);
      }
    },
    [setBlockedItems, excludedItems, onItemClick]
  );

  const clickOnCheck = useCallback(
    (event: React.MouseEvent<HTMLButtonElement, MouseEvent>, item: any) => {
      event.stopPropagation();
      setBlockedItems((oldBlocked) => {
        const newBlocked = new Set(oldBlocked);
        newBlocked.delete(item.id);

        return newBlocked;
      });
    },
    [setBlockedItems]
  );

  return (
    <div style={{marginBottom: 40}}>
      <ListItem
        style={{
          marginBottom: 10,
        }}
      >
        <TextField
          fullWidth
          size="small"
          variant="outlined"
          style={{marginTop: 20}}
          onChange={handleSearch}
          classes={{
            root: mode === "dark" ? classes.searchDark : classes.search,
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search style={{ color: mode === 'light' ? "black" : 'white' }} />
              </InputAdornment>
            ),
          }}
        />
      </ListItem>
      <List disablePadding>
      <ListItem
            style={{height: 32, margin: 0, padding: "2px 0 2px 16px" }}
            key="on-all"
            onClick={() => excludedItems.size > 0 ? onAll() : onNone()}
          >
          {excludedItems.size ===  itemList.length ? 
            <Checkbox style={{ color: "white"}} checked={false} />
            :
            <Checkbox
              style={{ color: "white"}}
              checked={excludedItems.size === 0}
              indeterminate={excludedItems.size > 0}
            />
            
          }          
            <ListItemText primary={excludedItems.size > 0 ? "(Select All)" : '(Select None)'} />
           
          </ListItem>
        {[...filteredItems, ...blockedFitSearch]
         .slice(
          (currentPage - 1) * itemsPerPage,
          currentPage * itemsPerPage
        )
        .map((item) => (
          blockedItems.has(item.id) ? 
          <ListItem key={item.id} style={{height: 32, margin: 0, padding: "2px 0 2px 16px" }}>
            <Checkbox
              style={{ visibility: "hidden", paddingTop: 0, paddingBottom: 0 }}
            />
            <ListItemText primary={formatDisplay(item)} />
            <IconButton
              role="button"
              size="small"
              onClick={(event) => clickOnCheck(event, item)}
            >
              <Check style={{ color: "white" }} />
            </IconButton>
          </ListItem>
          :
          <ListItem
            style={{height: 32, margin: 0, padding: "2px 0 2px 16px" }}
            key={item.id}
            onClick={() => onItemClick(item)}
          >
            <Checkbox
              style={{
                color: "white",
              }}
              checked={excludedItems && !excludedItems?.has(item.id)}
            />
            <ListItemText primary={formatDisplay(item)} />
            <IconButton
              size="small"
              onClick={(event) => clickOnBlock(event, item)}
            >
              <Block
                style={{
                  color: "white",
                }}
              />
            </IconButton>
          </ListItem>
        ))}
       
        <div className={`sidebar-legacy-pagination-container ${mode === 'light' ? "light-pagination" : "dark-pagination"}`}>
        <Pagination count={totalPages} page={currentPage} size="small" color="primary" onChange={handlePageChange}/>
        </div>
      </List>
    </div>
  );
}
